<template>
  <div class="min-h-screen bg-gray-50">
    <!-- 复用Header组件 -->
    <Header />

    <!-- 主要内容 -->
    <div class="container mx-auto py-6">
      <!-- 面包屑导航 -->
      <div class="mb-6">
        <div class="flex items-center text-sm bg-white px-4 py-3 rounded-lg shadow-sm">
          <router-link to="/" class="text-gray-500 hover:text-blue-600">网站首页</router-link>
          <span class="mx-2 text-gray-400">/</span>
          <router-link v-if="!isAboutOrBranch" :to="getFirstLevelCategoryPath()" class="text-gray-500 hover:text-blue-600">{{ firstLevelCategoryName }}</router-link>
          <span v-else class="text-gray-400">{{ firstLevelCategoryName }}</span>
          <span class="mx-2 text-gray-400">/</span>
          <router-link v-if="!isAboutOrBranch" :to="getParentCategoryPath()" class="text-gray-500 hover:text-blue-600">{{ parentCategoryName }}</router-link>
          <span v-else class="text-gray-400">{{ parentCategoryName }}</span>
          <span class="mx-2 text-gray-400">/</span>
          <span class="text-blue-600 font-medium">{{ article ? article.title : '新闻详情' }}</span>
        </div>
      </div>

      <div class="flex justify-center">
        <!-- 文章内容区域 -->
        <div class="max-w-3xl w-full">
          <div class="bg-white p-6 md:p-8 rounded-lg shadow-sm">
            <!-- 加载状态 -->
            <div v-if="loading" class="flex justify-center items-center py-20">
              <div class="text-gray-500">加载中...</div>
            </div>

            <!-- 错误状态 -->
            <div v-else-if="error" class="flex justify-center items-center py-20">
              <div class="text-red-500">{{ error }}</div>
            </div>

            <!-- 文章内容 -->
            <div v-else-if="article" class="space-y-6">
              <!-- 文章标题 -->
              <h1 class="article-title">{{ article.title }}</h1>

              <!-- 文章元信息 -->
              <div class="article-meta">
                <div><i class="far fa-calendar-alt mr-2"></i> 日期: {{ formatDate(article.publishedTime) }}</div>
                <div><i class="fa fa-eye mr-2"></i> 浏览量: {{ articleViews.toLocaleString() }}</div>
              </div>

              <!-- 文章正文 -->
              <div class="article-content" v-html="article.contentHtml"></div>
            </div>

            <!-- 暂无数据 -->
            <div v-else class="flex justify-center items-center py-20">
              <div class="text-gray-500">暂无文章数据</div>
            </div>


          </div>
        </div>
      </div>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import AppFooter from '../components/Footer.vue'
import { api } from '../api'
import { useHead } from '@unhead/vue'
import { ref, computed } from 'vue'

export default {
  name: 'ArticleDetail',
  components: {
    Header,
    AppFooter
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  setup() {
    const pageTitle = ref('吉林省软件行业协会')
    const pageDescription = ref('')
    const pageImage = ref('')

    useHead({
      title: pageTitle,
      meta: computed(() => {
        const meta = []
        meta.push({ property: 'og:title', content: pageTitle.value })
        if (pageDescription.value) {
          meta.push({ property: 'og:description', content: pageDescription.value })
          meta.push({ name: 'description', content: pageDescription.value })
        }
        if (pageImage.value) {
          meta.push({ property: 'og:image', content: pageImage.value })
        }
        meta.push({ property: 'og:type', content: 'article' })
        return meta
      })
    })

    return { pageTitle, pageDescription, pageImage }
  },
  data() {
    return {
      article: null,
      articleViews: 0,
      categories: [],
      loading: false,
      error: null,
      relatedArticles: [
        {
          id: 2,
          title: '全省中小企业数字化线上大讲堂第二期活动成功举行',
          publishedTime: '2022-07-15T10:00:00'
        },
        {
          id: 3,
          title: '吉林省软件行业协会2022年上半年工作总结',
          publishedTime: '2022-07-10T14:30:00'
        },
        {
          id: 4,
          title: '吉林省软件企业评估工作启动会议召开',
          publishedTime: '2022-06-25T09:15:00'
        }
      ]
    }
  },
  computed: {
    isAboutOrBranch() {
      if (!this.article || !this.categories.length) {
        return false
      }

      const category = this.categories.find(cat => cat.id === this.article.categoryId)
      if (category) {
        return category.firstLevelModule === '关于协会' || category.firstLevelModule === '分支机构'
      }

      return false
    },
    firstLevelCategoryName() {
      if (!this.article || !this.categories.length) {
        return '协会动态'
      }

      const category = this.categories.find(cat => cat.id === this.article.categoryId)
      if (category) {
        return category.firstLevelModule
      }

      return '协会动态'
    },
    parentCategoryName() {
      if (!this.article || !this.categories.length) {
        return '通知公告'
      }

      const category = this.categories.find(cat => cat.id === this.article.categoryId)
      if (category) {
        return category.secondLevelModule
      }

      return '通知公告'
    }
  },
  mounted() {
    this.loadArticleDetail()
    this.loadCategories()
  },
  methods: {
    async loadArticleDetail() {
      this.loading = true
      this.error = null
      try {
        const article = await api.getArticleDetail(this.id)
        this.article = article
        this.articleViews = article.clickCount
        this.pageTitle = article.title || '吉林省软件行业协会'
        this.pageDescription = article.summary || ''
        this.pageImage = article.coverImageUrl || ''
      } catch (error) {
        this.error = '加载文章详情失败: ' + error.message
        console.error('加载文章详情失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const categories = await api.getCategories()
        this.categories = categories
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    },
    getFirstLevelCategoryPath() {
      if (!this.article || !this.categories.length) {
        return '/notices'
      }

      const category = this.categories.find(cat => cat.id === this.article.categoryId)
      if (category) {
        const firstLevelName = category.firstLevelModule
        if (firstLevelName === '服务项目') {
          return '/services'
        } else if (firstLevelName === '协会动态') {
          return '/notices'
        } else if (firstLevelName === '会员风采' || firstLevelName === '关于协会' || firstLevelName === '分支机构') {
          return '/'
        }
      }
      return '/notices'
    },
    getParentCategoryPath() {
      if (!this.article || !this.categories.length) {
        return '/notices'
      }

      const category = this.categories.find(cat => cat.id === this.article.categoryId)
      if (category) {
        const firstLevelName = category.firstLevelModule
        const secondLevelName = category.secondLevelModule

        if (firstLevelName === '服务项目') {
          return `/services?category=${encodeURIComponent(secondLevelName)}`
        } else if (firstLevelName === '协会动态') {
          return `/notices?category=${encodeURIComponent(secondLevelName)}`
        } else if (firstLevelName === '会员风采' || firstLevelName === '关于协会' || firstLevelName === '分支机构') {
          return '/'
        }
      }
      return '/notices'
    },
    formatDate(dateString) {
      if (!dateString) return '未知时间'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    formatDay(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.getDate()
    },
    formatMonth(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return `${date.getMonth() + 1}月`
    }
  }
}
</script>

<style scoped>
.article-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  line-height: 1.3;
  text-align: center;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
  color: #64748b;
  font-size: 14px;
}

.article-content {
  font-size: 16px;
  line-height: 1.8;
  color: #374151;
}

.article-content p {
  margin-bottom: 20px;
  text-indent: 2em;
}

.article-content img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 24px auto;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.related-articles {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 2px solid #f0f4f8;
}

.related-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
}

.related-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s ease;
}

.related-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.related-item:hover {
  transform: translateX(4px);
}

.related-date {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 8px;
  padding: 10px 8px;
  text-align: center;
  min-width: 56px;
  margin-right: 16px;
  flex-shrink: 0;
}

.related-day {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1;
}

.related-month {
  font-size: 11px;
  color: #64748b;
  margin-top: 3px;
}

.related-content {
  flex: 1;
}

.related-link {
  color: #1e293b;
  font-weight: 500;
  text-decoration: none;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.related-link:hover {
  color: #3b82f6;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .article-title {
    font-size: 24px;
  }

  .article-content {
    font-size: 15px;
  }
}
</style>
